import { useCallback, useEffect } from 'react';
import {
  useAppDispatch,
  useAppSelector,
  useUserPermissions,
} from '@Hooks/index';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import {
  RequestPathPerType,
  RequestTypePerPath,
} from '@Features/router/mr/enum/RequestsRoutePaths.enum';
import {
  currentActionComponent,
  currentSelectedRequest,
  getRequisitionRequestTypeOptions,
} from '@Features/store/selectors/manageRequestSelector';
import { manageRequestActions } from '@Features/store/ManageRequestSlice';
import { useGetRequisitionRequestsCountMutation } from '@Store/api/basicHrmApiSlice';
import { headerActions } from '@Store/slices/headerSlice';
import { PresentationRequestHeader } from '@Components/index';
import { extractRequestTypeCode } from '@Features/utils/extracterPathnames';

const Procurement: React.FC = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();
  const selectedRequest = useAppSelector(currentSelectedRequest);
  const ActionComponent = useAppSelector(currentActionComponent);
  const requestTypesOptions = useAppSelector(getRequisitionRequestTypeOptions);
  const permissions = useUserPermissions();

  const [getRequestsCount] = useGetRequisitionRequestsCountMutation();

  useEffect(() => {
    const requestTypeCode = extractRequestTypeCode(
      pathname,
      RequestPathPerType
    );

    const requestTypeLabel = requestTypesOptions.find(
      (requestTypesOption) => requestTypesOption?.value === requestTypeCode
    )?.label;

    dispatch(manageRequestActions.setSelectedRequest(requestTypeLabel || null));
  }, [pathname, requestTypesOptions]);

  const fetchRequestsCount = useCallback(() => {
    getRequestsCount()
      .unwrap()
      .then((response) => {
        dispatch(headerActions.setRequestsCount(response));
      });
  }, []);

  const handleSelect = useCallback(
    (value: string) => {
      const selectedOption = requestTypesOptions.find(
        (requestTypesOption) => requestTypesOption.label === value
      );

      if (!selectedOption) {
        return;
      }

      navigate(`/mr/procurement/${RequestTypePerPath[selectedOption?.value]}`);
    },
    [requestTypesOptions]
  );

  return (
    <>
      {!isRequestFormPage &&
        permissions.can('Search', 'PurchaseRequest') &&
        permissions.can('Search', 'TenderRequest') && (
          <PresentationRequestHeader
            selectOptions={requestTypesOptions}
            selectedValue={selectedRequest}
            caption="Procurement"
            handleSelect={handleSelect}
            onRequestSelectorOpen={fetchRequestsCount}
            showSelect={permissions.can('Search', 'TenderRequest')}
          >
            {ActionComponent}
          </PresentationRequestHeader>
        )}
      <Outlet context={{ isRequestFormPage }} />
    </>
  );
};

export default Procurement;
